<script setup lang="ts">
  import type { FetchDataOptions, PaginatedResponse } from '@/components/admin/PaginatedTable.vue'
  import PaginatedTableView from '@/components/admin/PaginatedTable.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import DocumentTypeBadge from '@/components/app/cases/documents/DocumentTypeBadge.vue'
  import DocumentUpload from '@/components/app/cases/documents/DocumentUpload.vue'
  import PlatformFilterDropdown from '@/components/app/cases/documents/PlatformFilterDropdown.vue'
  import PlatformStatusBadge from '@/components/app/cases/documents/PlatformStatusBadge.vue'
  import SyncStatusBanner from '@/components/app/cases/documents/SyncStatusBanner.vue'
  import TemplateDialog from '@/components/app/cases/documents/TemplateDialog.vue'
  import {
    FILTER_ALL,
    type PlatformFilterValue
  } from '@/components/app/cases/documents/platformFilterTypes'
  import ActaportDarkIcon from '@/components/icons/ActaportDarkIcon.vue'
  import AiEditIcon from '@/components/icons/AiEditIcon.vue'
  import MergeIcon from '@/components/icons/mergeIcon.vue'
  import NewPdfViewer from '@/components/pdf/NewPdfViewer.vue'
  import PDFViewer from '@/components/pdf/pdfViewer.vue'
  import { Badge } from '@/components/ui/badge'
  import { Button } from '@/components/ui/button'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
  } from '@/components/ui/dropdown-menu'
  import { TableCell, TableRow } from '@/components/ui/table'
  import { useToast } from '@/components/ui/toast'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
  import { useCaseChannelSubscription } from '@/composables/useChannelSubscription'
  import { isDocumentReadOnly } from '@/composables/useDocumentProcessing'
  import { useFeatureFlag } from '@/composables/useFeatureFlag'
  import { MetricEventType, useMetrics } from '@/composables/useMetrics'
  import { useSyncNotifications } from '@/composables/useSyncNotifications'
  import { useTranslation } from '@/composables/useTranslation'
  import { documentTypesDE, documentTypesEN } from '@/i18n/documentTypes'
  import { uploadOriginsDE, uploadOriginsEN } from '@/i18n/uploadOrigins'
  import { genericSuccessToast } from '@/lib/toasts'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import {
    type Document,
    DocumentsService,
    DocumentStatusEnum,
    DocumentTypeEnum,
    FlagEnum,
    FormsService,
    PlatformSyncStatusEnum,
    QuestionnairesService,
    SendToActaportResponseStatusEnum
  } from '@/types/generated'
  import {
    type DocumentActaportBulkSyncData,
    type DocumentActaportImportData,
    type DocumentActaportSyncData,
    WebSocketMessageType
  } from '@/utils/channels/types'
  import { copyToClipboard } from '@/utils/clipboard'
  import {
    ArrowLeftRight,
    CheckCircle2,
    ChevronDown,
    CloudUpload,
    Download,
    Eye,
    FileQuestion,
    FileStack,
    FileText,
    Link2,
    Loader,
    Lock,
    MoreVertical,
    Pencil,
    RefreshCw,
    Trash2,
    X
  } from 'lucide-vue-next'
  import { computed, nextTick, onMounted, ref, watch } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import HelpLink from '@/components/app/HelpLink.vue'
  import { KNOWLEDGE_BASE_URLS } from '@/constants/knowledgeBaseUrls'
  import { MAX_DOCUMENT_SIZE_BYTES } from '@/components/app/cases/organize-documents/composables/useDocumentState'

  // Extended Document type to include actaport_id, actaport_url, and is_virtual for virtual documents
  type DocumentWithActaport = Document & {
    actaport_id?: string
    actaport_url?: string
    is_virtual?: boolean
  }

  const router = useRouter()
  const route = useRoute()
  const { toast } = useToast()
  const syncNotifications = useSyncNotifications()
  const store = useCaseStore()
  const { trackEvent } = useMetrics()
  const tableRef = ref()
  const editingDocument = ref<string | null>(null)
  const newName = ref('')
  const virtualDocuments = ref<DocumentWithActaport[]>([])
  const isRefreshingActaport = ref(false)
  const lastActaportRefresh = ref<Date | null>(null)
  const showDeleteModal = ref(false)
  const documentToDelete = ref<Document | null>(null)
  const showBulkDeleteModal = ref(false)
  const showMergeSizeWarning = ref(false)
  const mergeSizeValidation = ref<{
    estimatedSize: string
    maxSize: string
    exceedsBy: string
  } | null>(null)
  const isGeneratingTitle = ref(false)
  const isBulkRenaming = ref(false)

  const showPdfViewer = ref(false)
  const currentPdfUrl = ref('')
  const currentPdfFilename = ref('')
  const currentPdfUuid = ref('')
  const currentDocument = ref<any>(null)

  const showUploadModal = ref(false)

  const actaportV2TwoWaySyncEnabled = useFeatureFlag(FlagEnum.ACTAPORT_V2_TWO_WAY_SYNC)

  const showPlatformStatusColumn = computed(() => {
    return actaportV2TwoWaySyncEnabled.isEnabled.value && !!store.caseRecord?.actaport_case_id
  })

  const enableLawSpecificData = computed(() => {
    return store.caseRecord?.org?.enable_law_specific_data ?? false
  })

  const caseUuid = computed(() => store.caseRecord?.uuid || '')
  const { lastMessage } = useCaseChannelSubscription(caseUuid)

  const syncingDocuments = ref<Set<string>>(new Set())
  const importingDocuments = ref<Set<string>>(new Set())

  watch(lastMessage, async (message) => {
    if (!message) return

    switch (message.type) {
      case WebSocketMessageType.DOCUMENT_ACTAPORT_SYNC: {
        const data = message.data as DocumentActaportSyncData

        if (data.status === 'success') {
          if (store.documents) {
            const docIndex = store.documents.findIndex(
              (d: Document) => d.uuid === data.document_uuid
            )
            if (docIndex !== -1) {
              store.documents[docIndex] = {
                ...store.documents[docIndex],
                platform_sync_status: PlatformSyncStatusEnum.IN_BOTH
              }
            }
          }

          await tableRef.value?.refreshData()
          syncingDocuments.value.delete(data.document_uuid)

          // Use banner notification instead of toast
          syncNotifications.completeOperation(true)
        } else if (data.status === 'failed') {
          syncingDocuments.value.delete(data.document_uuid)

          // Find the document for retry
          const failedDoc = store.documents?.find((d: Document) => d.uuid === data.document_uuid)

          // Use banner notification with failed operation details for retry
          syncNotifications.completeOperation(
            false,
            data.error || 'Failed to send document to Actaport',
            failedDoc
              ? {
                  type: 'send',
                  documentUuid: data.document_uuid,
                  document: failedDoc
                }
              : undefined
          )
        }
        break
      }

      case WebSocketMessageType.DOCUMENT_ACTAPORT_IMPORT: {
        const data = message.data as DocumentActaportImportData

        if (data.status === 'success') {
          await store.loadCase(caseUuid.value)
          await tableRef.value?.refreshData()
          importingDocuments.value.delete(data.actaport_document_id)

          // Use banner notification instead of toast
          syncNotifications.completeOperation(true)
        } else if (data.status === 'failed') {
          importingDocuments.value.delete(data.actaport_document_id)

          // Find the Actaport document for retry
          const failedActaportDoc = virtualDocuments.value.find(
            (d: DocumentWithActaport) => d.actaport_id === data.actaport_document_id
          )

          // Use banner notification with failed operation details for retry
          syncNotifications.completeOperation(
            false,
            data.error || t('importErrorDescription'),
            failedActaportDoc
              ? {
                  type: 'import',
                  actaportId: data.actaport_document_id,
                  document: failedActaportDoc
                }
              : undefined
          )
        }
        break
      }

      case WebSocketMessageType.DOCUMENT_ACTAPORT_BULK_SYNC: {
        const data = message.data as DocumentActaportBulkSyncData

        if (data.status === 'started') {
          // Bulk sync started successfully
          // The total_count is handled by handleBulkSyncPlatforms
        } else if (data.status === 'failed') {
          // Bulk sync failed to start - all operations failed
          const totalCount = data.total_count || 0
          for (let i = 0; i < totalCount; i++) {
            syncNotifications.completeOperation(false, data.error || 'Failed to start bulk sync')
          }
        }
        break
      }

      case WebSocketMessageType.CASE_DOCUMENT_PROCESSING: {
        const data = message.data as any
        if (data?.document_uuid && data?.status === 'FINISHED') {
          // Refresh table to show updated status
          await tableRef.value?.refreshData()
        }
        break
      }

      default:
        break
    }
  })

  const handleFileUploaded = async (documentUuid: string) => {
    showUploadModal.value = false

    // Refresh the case data to get the newly uploaded document
    await store.loadCase(store.caseRecord?.uuid || '')

    // Refresh the table data to show the updated documents
    if (tableRef.value) {
      tableRef.value.refreshData()
    }
  }

  // Filter states
  const showReadOnlyDocuments = ref(true)
  const selectedDocuments = ref<Document[]>([])

  const platformFilter = ref<PlatformFilterValue>(
    (route.query.platformFilter as PlatformFilterValue) || FILTER_ALL
  )

  const { t } = useTranslation({
    en: {
      title: 'Documents',
      createLink: 'Create Link',
      generateDocument: 'Generate Document',
      uploadDocuments: 'Upload Documents',
      showReadOnlyDocuments: 'Show read-only documents',
      uploadDocumentsDescription:
        'Upload documents to this case by dragging and dropping or browsing your files.',
      received: 'Received',
      requested: 'Requested',
      allStatuses: 'All Statuses',
      document: 'Document',
      questionnaire: 'Questionnaire',
      name: 'Name',
      status: 'Status',
      platformStatus: 'Platform Status',
      type: 'Type',
      source: 'Source',
      date: 'Date',
      actions: 'Actions',
      preview: 'Preview',
      download: 'Download',
      rename: 'Rename',
      organizePages: 'Organize Pages',
      delete: 'Delete',
      sendToActaport: 'Send to Actaport',
      sendingToActaport: 'Sending...',
      bulkSendToActaport: 'Send to Actaport',
      refreshFromActaport: 'Refresh from Actaport',
      refreshing: 'Refreshing...',
      refreshSuccess: 'Documents Refreshed',
      refreshSuccessDescription: 'Latest documents fetched from Actaport',
      refreshError: 'Refresh Failed',
      refreshErrorDescription: 'Could not fetch documents from Actaport',
      bulkSending: 'Sending Documents',
      bulkSendSuccess: 'Send Started',
      bulkSendError: 'Send Error',
      bulkSyncPlatforms: 'Sync Between Platforms',
      bulkSyncing: 'Syncing Documents',
      bulkSyncSuccess: 'Sync Started',
      bulkSyncError: 'Sync Error',
      importFromActaport: 'Import from Actaport',
      importingFromActaport: 'Importing...',
      bulkImportFromActaport: 'Import from Actaport',
      bulkImporting: 'Importing Documents',
      bulkImportSuccess: 'Import Started',
      bulkImportError: 'Import Error',
      allPlatforms: 'All Platforms',
      onlyInJupus: 'Only in Jupus',
      onlyInActaport: 'Only in Actaport',
      inBothPlatforms: 'In Both Platforms',
      importSuccess: 'Document Imported',
      importSuccessDescription: 'Document has been imported from Actaport',
      importError: 'Import Failed',
      importErrorDescription: 'Failed to import document from Actaport',
      deleteTitle: 'Delete Confirmation',
      description: 'Are you sure you want to delete this document? This action cannot be undone.',
      cancel: 'Cancel',
      confirm: 'Delete',
      selectField: 'Select...',
      documentTemplate: 'Document Template',
      documentTemplateTitle: 'Document Template',
      documentTemplateDescription:
        'Copy a link to the desired document and send it to the customers.',
      documentTemplateSearchPlaceholder: 'Search document template',
      noResults: 'No results found',
      copyLink: 'Copy Link',
      untitledDocument: 'Untitled Document',
      close: 'Close',
      selected: 'selected',
      documents: 'documents',
      renameWithAI: 'Rename with AI',
      mergeDocuments: 'Merge Documents',
      bulkDelete: 'Delete',
      bulkDeleteTitle: 'Delete Multiple Documents',
      bulkDeleteDescription:
        'Are you sure you want to delete {count} selected document(s)? This action cannot be undone.',
      bulkDeleteSuccess: 'Documents deleted successfully',
      bulkDeleteError: 'Failed to delete some documents',
      bulkRename: 'Rename with AI',
      bulkRenameSuccess: 'Documents renamed successfully',
      bulkRenameError: 'Failed to rename some documents',
      readyForAI: 'Ready for AI',
      readyForAIDescription: 'You can ask questions about this document',
      aiTitleGenerated: 'AI Title Generated',
      aiTitleGeneratedDescription: 'AI has generated a new title for this document',
      aiTitleError: 'AI Title Error',
      aiTitleErrorDescription: 'Failed to generate AI title',
      downloading: 'Downloading',
      preparingFiles: 'Preparing files for download...',
      downloadComplete: 'Download Complete',
      filesDownloadedSuccessfully: 'Files have been downloaded successfully',
      downloadError: 'Download Error',
      bulkDownloadFailed: 'Failed to download files. Please try again.',
      filesTooLarge: 'Selected files are too large for bulk download',
      error: 'Error',
      organizePagesDescription: 'Please select at least 2 documents to organize',
      organizePagesError: 'Failed to navigate to organize pages',
      cannotDeleteReadOnly: 'Cannot delete - read-only document selected',
      cannotMergeReadOnly: 'Cannot merge - read-only document selected',
      cannotMergeSingle: 'Select at least 2 documents to merge',
      readOnlyDocument: 'Read-only generated document',
      helpDocuments: 'Learn more about Documents',
      mergeSizeWarning: 'Document Size Limit Warning',
      mergeSizeWarningDescription:
        'Merging these documents would create a file of approximately {estimatedSize}, which exceeds the maximum allowed size of {maxSize}. The save operation may fail if you proceed.',
      mergeSizeExceedsBy: 'Exceeds limit by: {size}',
      proceedAnyway: 'Merge anyway',
      cancelMerge: 'Cancel',
      ...Object.fromEntries(Object.entries(documentTypesEN).map(([key, value]) => [key, value])),
      ...Object.fromEntries(Object.entries(uploadOriginsEN).map(([key, value]) => [key, value]))
    },
    de: {
      title: 'Dokumente',
      createLink: 'Link erzeugen',
      generateDocument: 'Dokument generieren',
      uploadDocuments: 'Dokumente hochladen',
      showReadOnlyDocuments: 'Schreibgeschützte Dokumente anzeigen',
      uploadDocumentsDescription:
        'Laden Sie Dokumente in diesen Fall hoch, indem Sie sie per Drag & Drop hinzufügen oder Ihre Dateien durchsuchen.',
      received: 'Erhalten',
      requested: 'Angefordert',
      allStatuses: 'Alle Status',
      document: 'Dokument',
      questionnaire: 'Fragebogen',
      name: 'Name',
      status: 'Status',
      platformStatus: 'Plattformstatus',
      type: 'Typ',
      source: 'Quelle',
      date: 'Datum',
      actions: 'Aktionen',
      preview: 'Vorschau',
      download: 'Herunterladen',
      rename: 'Umbenennen',
      organizePages: 'Seiten organisieren',
      delete: 'Löschen',
      sendToActaport: 'An Actaport senden',
      sendingToActaport: 'Wird gesendet...',
      bulkSendToActaport: 'An Actaport senden',
      refreshFromActaport: 'Von Actaport aktualisieren',
      refreshing: 'Aktualisierung...',
      refreshSuccess: 'Dokumente aktualisiert',
      refreshSuccessDescription: 'Neueste Dokumente von Actaport abgerufen',
      refreshError: 'Aktualisierung fehlgeschlagen',
      refreshErrorDescription: 'Dokumente konnten nicht von Actaport abgerufen werden',
      bulkSending: 'Dokumente senden',
      bulkSendSuccess: 'Senden gestartet',
      bulkSendError: 'Senden-Fehler',
      bulkSyncPlatforms: 'Zwischen Plattformen synchronisieren',
      bulkSyncing: 'Dokumente synchronisieren',
      bulkSyncSuccess: 'Synchronisierung gestartet',
      bulkSyncError: 'Synchronisierungsfehler',
      importFromActaport: 'Von Actaport importieren',
      importingFromActaport: 'Importiert...',
      bulkImportFromActaport: 'Von Actaport importieren',
      bulkImporting: 'Dokumente importieren',
      bulkImportSuccess: 'Import gestartet',
      bulkImportError: 'Import-Fehler',
      allPlatforms: 'Alle Plattformen',
      onlyInJupus: 'Nur in Jupus',
      onlyInActaport: 'Nur in Actaport',
      inBothPlatforms: 'In beiden Plattformen',
      importSuccess: 'Dokument importiert',
      importSuccessDescription: 'Dokument wurde von Actaport importiert',
      importError: 'Import fehlgeschlagen',
      importErrorDescription: 'Fehler beim Importieren des Dokuments von Actaport',
      deleteTitle: 'Bestätigung löschen',
      description:
        'Sind Sie sicher, dass Sie das Dokument löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
      cancel: 'Abbrechen',
      confirm: 'Löschen',
      selectField: 'Auswählen...',
      documentTemplate: 'Dokumentenvorlage',
      documentTemplateTitle: 'Dokumentenvorlage',
      documentTemplateDescription:
        'Kopieren Sie einen Link zum gewünschten Dokument und senden Sie ihn an die Kunden.',
      documentTemplateSearchPlaceholder: 'Dokumentenvorlage suchen',
      noResults: 'Keine Ergebnisse gefunden',
      copyLink: 'Link kopieren',
      untitledDocument: 'Unbenanntes Dokument',
      close: 'Schließen',
      selected: 'ausgewählt',
      documents: 'Dokumente',
      renameWithAI: 'Mit KI umbenennen',
      mergeDocuments: 'Dokumente zusammenführen',
      bulkDelete: 'Löschen',
      bulkDeleteTitle: 'Mehrere Dokumente löschen',
      bulkDeleteDescription:
        'Sind Sie sicher, dass Sie {count} ausgewählte(s) Dokument(e) löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
      bulkDeleteSuccess: 'Dokumente erfolgreich gelöscht',
      bulkDeleteError: 'Einige Dokumente konnten nicht gelöscht werden',
      bulkRename: 'Mit KI umbenennen',
      bulkRenameSuccess: 'Dokumente erfolgreich umbenannt',
      bulkRenameError: 'Einige Dokumente konnten nicht umbenannt werden',
      readyForAI: 'Bereit für KI',
      readyForAIDescription: 'Sie können Fragen zu diesem Dokument stellen',
      aiTitleGenerated: 'KI-Titel generiert',
      aiTitleGeneratedDescription: 'KI hat einen neuen Titel für dieses Dokument generiert',
      aiTitleError: 'KI-Titel Fehler',
      aiTitleErrorDescription: 'KI-Titel konnte nicht generiert werden',
      downloading: 'Herunterladen',
      preparingFiles: 'Dateien für Download vorbereiten...',
      downloadComplete: 'Download abgeschlossen',
      filesDownloadedSuccessfully: 'Dateien wurden erfolgreich heruntergeladen',
      downloadError: 'Download Fehler',
      bulkDownloadFailed:
        'Dateien konnten nicht heruntergeladen werden. Bitte versuchen Sie es erneut.',
      filesTooLarge: 'Ausgewählte Dateien sind zu groß für den Bulk-Download',
      error: 'Fehler',
      organizePagesDescription:
        'Bitte wählen Sie mindestens 2 Dokumente aus, um sie zu organisieren',
      organizePagesError: 'Fehler beim Navigieren zur Seitenorganisation',
      cannotDeleteReadOnly: 'Löschen nicht möglich - schreibgeschütztes Dokument ausgewählt',
      cannotMergeReadOnly: 'Zusammenführen nicht möglich - schreibgeschütztes Dokument ausgewählt',
      cannotMergeSingle: 'Wählen Sie mindestens 2 Dokumente zum Zusammenführen aus',
      readOnlyDocument: 'Schreibgeschütztes generiertes Dokument',
      helpDocuments: 'Mehr über Dokumente erfahren',
      mergeSizeWarning: 'Warnung zur Dokumentgröße',
      mergeSizeWarningDescription:
        'Das Zusammenführen dieser Dokumente würde eine Datei von ungefähr {estimatedSize} erstellen, was die maximal erlaubte Größe von {maxSize} überschreitet. Der Speichervorgang kann fehlschlagen, wenn Sie fortfahren.',
      mergeSizeExceedsBy: 'Überschreitung: {size}',
      proceedAnyway: 'Trotzdem zusammenführen',
      cancelMerge: 'Abbrechen',
      ...Object.fromEntries(Object.entries(documentTypesDE).map(([key, value]) => [key, value])),
      ...Object.fromEntries(Object.entries(uploadOriginsDE).map(([key, value]) => [key, value]))
    }
  })

  const filterDocuments = (documents: Document[]): Document[] => {
    return documents.filter((doc) => {
      // Exclude documents with REQUESTED status
      // This includes:
      // - Questionnaires that haven't been filled yet
      // - Document templates that haven't been completed yet
      // - Any other documents in requested state
      // Note: Completed questionnaires and templates (RECEIVED/COMPLETED status) should be shown
      if (doc.document_status === DocumentStatusEnum.REQUESTED) {
        return false
      }

      // Apply filter based on checkbox
      const isReadOnly = isDocumentReadOnly(doc)

      // If showReadOnlyDocuments is true, show all documents
      // If showReadOnlyDocuments is false, only show editable documents (exclude read-only)
      if (!showReadOnlyDocuments.value && isReadOnly) {
        return false
      }

      return true
    })
  }

  const fetchDocuments = async (options?: FetchDataOptions): Promise<PaginatedResponse<any>> => {
    try {
      // Merge regular documents with virtual documents
      const jupusDocuments = store.documents || []
      const allDocuments = [...jupusDocuments, ...virtualDocuments.value]

      const filtered = filterDocuments(allDocuments).filter((doc) => {
        const matchesSearch =
          !options?.search || doc.filename.toLowerCase().includes(options.search.toLowerCase())

        const matchesPlatformFilter =
          platformFilter.value === FILTER_ALL || doc.platform_sync_status === platformFilter.value

        return matchesSearch && matchesPlatformFilter
      })

      const sorted = filtered.sort((a, b) => {
        const dateA = new Date(a.created_at).getTime()
        const dateB = new Date(b.created_at).getTime()
        return dateB - dateA
      })

      // Implement pagination
      const limit = options?.limit || 20
      const offset = options?.offset || 0
      const paginatedResults = sorted.slice(offset, offset + limit)

      return {
        results: paginatedResults,
        count: sorted.length,
        next: offset + limit < sorted.length ? 'next' : null,
        previous: offset > 0 ? 'previous' : null
      }
    } catch (error) {
      console.error('Error fetching documents:', error)
      throw error
    }
  }

  const isDocumentAiReady = (doc: Document): boolean => {
    // Simplified: Only check if metadata enrichment is finished
    // This is what we care about for showing the green checkmark
    return doc.metadata_enrichment_is_finished === true
  }

  const handleDocumentGeneration = () => {
    if (store.caseRecord?.uuid) {
      trackEvent(MetricEventType.AI_FUNCTION_DOCUMENT_GENERATION_USED, {
        source: 'case_documents'
      })
      router.push(`/app/cases/${store.caseRecord.uuid}/document-generation`)
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const startEditing = (doc: any) => {
    editingDocument.value = doc.uuid
    newName.value = doc.filename.replace('.pdf', '')

    // Track inline edit started
    trackEvent(MetricEventType.DOCUMENT_INLINE_EDIT_STARTED, {
      document_uuid: doc.uuid,
      document_name: doc.filename,
      document_type: doc.document_type,
      case_uuid: store.caseRecord?.uuid
    })
  }

  const cancelEditing = () => {
    const docUuid = editingDocument.value
    editingDocument.value = null
    newName.value = ''

    // Track rename cancelled
    if (docUuid) {
      trackEvent(MetricEventType.DOCUMENT_RENAME_CANCELLED, {
        document_uuid: docUuid,
        case_uuid: store.caseRecord?.uuid
      })
    }
  }

  const saveNewName = async (doc: any) => {
    const oldName = doc.filename
    const newNameValue = newName.value

    try {
      const response = await DocumentsService.documentsUpdate({
        uuid: doc.uuid,
        requestBody: {
          filename: `${newName.value}.pdf`,
          title: newName.value
        }
      })

      // Update the document in the local store for instant UI feedback
      if (store.documents) {
        const docIndex = store.documents.findIndex((d: Document) => d.uuid === doc.uuid)
        if (docIndex !== -1) {
          store.documents[docIndex] = {
            ...store.documents[docIndex],
            filename: `${newName.value}.pdf`,
            title: newName.value
          }
        }
      }

      // Track rename saved
      trackEvent(MetricEventType.DOCUMENT_RENAME_SAVED, {
        document_uuid: doc.uuid,
        old_name: oldName,
        new_name: `${newNameValue}.pdf`,
        case_uuid: store.caseRecord?.uuid,
        used_ai: false
      })

      editingDocument.value = null
      newName.value = ''

      // Refresh the table to show the updated data
      if (tableRef.value) {
        tableRef.value.refreshData()
      }

      // Show success toast
      genericSuccessToast()
    } catch (error) {
      console.error('Error updating document:', error)
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'Dokument konnte nicht umbenannt werden'
      })
    }
  }

  const generateAITitle = async (doc: any) => {
    if (isGeneratingTitle.value) return

    isGeneratingTitle.value = true

    // Track AI rename clicked
    trackEvent(MetricEventType.DOCUMENT_RENAME_AI_CLICKED, {
      document_uuid: doc.uuid,
      document_name: doc.filename,
      case_uuid: store.caseRecord?.uuid
    })

    try {
      const response = await DocumentsService.documentsGenerateTitleCreate({
        uuid: doc.uuid
      })

      if (response.suggested_title) {
        newName.value = response.suggested_title

        // Track AI rename completed
        trackEvent(MetricEventType.DOCUMENT_RENAME_AI_COMPLETED, {
          document_uuid: doc.uuid,
          old_name: doc.filename,
          suggested_title: response.suggested_title,
          case_uuid: store.caseRecord?.uuid
        })
      }

      toast({
        title: t('aiTitleGenerated'),
        description: t('aiTitleGeneratedDescription'),
        variant: 'default'
      })
    } catch (error) {
      console.error('Error generating AI title:', error)

      // Track AI rename failed
      trackEvent(MetricEventType.DOCUMENT_RENAME_AI_FAILED, {
        document_uuid: doc.uuid,
        error: error instanceof Error ? error.message : 'Unknown error',
        case_uuid: store.caseRecord?.uuid
      })

      toast({
        variant: 'destructive',
        title: t('aiTitleError'),
        description: error instanceof Error ? error.message : t('aiTitleErrorDescription')
      })
    } finally {
      isGeneratingTitle.value = false
    }
  }

  const handleShowReadOnlyChange = () => {
    selectedDocuments.value = []
    // Use nextTick to ensure the reactive update happens before refreshing
    nextTick(() => {
      if (tableRef.value) {
        tableRef.value.refreshData()
      }
    })
  }

  const handleBulkDownload = async () => {
    if (selectedDocuments.value.length === 0) return

    // Check total size before downloading
    const MAX_TOTAL_SIZE = 500 * 1024 * 1024 // 500MB
    let totalSize = 0

    for (const doc of selectedDocuments.value) {
      totalSize += doc.filesize || 0
    }

    if (totalSize > MAX_TOTAL_SIZE) {
      toast({
        title: t('downloadError'),
        description: t('filesTooLarge'),
        variant: 'destructive'
      })
      return
    }

    // Track bulk download started
    trackEvent(MetricEventType.DOCUMENTS_BULK_DOWNLOAD_STARTED, {
      document_count: selectedDocuments.value.length,
      total_size_bytes: totalSize,
      case_uuid: store.caseRecord?.uuid,
      document_uuids: selectedDocuments.value.map((d) => d.uuid)
    })

    try {
      // Show loading state
      const loadingToast = toast({
        title: t('downloading'),
        description: t('preparingFiles')
      })

      // Create a ZIP file using JSZip
      const JSZip = (await import('jszip')).default
      const zip = new JSZip()

      // Download all selected documents and add them to the ZIP
      const downloadPromises = selectedDocuments.value.map(async (doc) => {
        try {
          const response = await fetch(doc.url)
          if (!response.ok) throw new Error(`Failed to fetch ${doc.filename}`)
          const blob = await response.blob()
          zip.file(doc.filename, blob)
        } catch (error) {
          console.error(`Error downloading ${doc.filename}:`, error)
          // Continue with other files even if one fails
        }
      })

      await Promise.all(downloadPromises)

      // Generate the ZIP file
      const zipBlob = await zip.generateAsync({ type: 'blob' })

      // Download the ZIP file
      const url = window.URL.createObjectURL(zipBlob)
      const link = document.createElement('a')
      link.href = url
      link.download = `selected_documents_${new Date().toISOString().split('T')[0]}.zip`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      // Close loading toast and show success
      loadingToast.dismiss()
      toast({
        title: t('downloadComplete'),
        description: t('filesDownloadedSuccessfully'),
        variant: 'default'
      })

      // Track bulk download completed
      trackEvent(MetricEventType.DOCUMENTS_BULK_DOWNLOAD_COMPLETED, {
        document_count: selectedDocuments.value.length,
        total_size_bytes: totalSize,
        case_uuid: store.caseRecord?.uuid
      })

      // Clear selection
      selectedDocuments.value = []
    } catch (error) {
      console.error('Error in bulk download:', error)

      // Track bulk download failed
      trackEvent(MetricEventType.DOCUMENTS_BULK_DOWNLOAD_FAILED, {
        document_count: selectedDocuments.value.length,
        error: error instanceof Error ? error.message : 'Unknown error',
        case_uuid: store.caseRecord?.uuid
      })

      toast({
        title: t('downloadError'),
        description: t('bulkDownloadFailed'),
        variant: 'destructive'
      })
    }
  }

  /**
   * Format bytes to human readable string
   */
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
  }

  /**
   * Calculate total size of selected documents for merge
   */
  const calculateMergeSize = (): number => {
    return selectedDocuments.value.reduce((sum, doc) => sum + (doc.filesize || 0), 0)
  }

  /**
   * Check if merge would exceed size limit
   */
  const checkMergeSizeLimit = (): boolean => {
    const totalSize = calculateMergeSize()
    if (totalSize > MAX_DOCUMENT_SIZE_BYTES) {
      const exceedsBy = totalSize - MAX_DOCUMENT_SIZE_BYTES
      mergeSizeValidation.value = {
        estimatedSize: formatFileSize(totalSize),
        maxSize: formatFileSize(MAX_DOCUMENT_SIZE_BYTES),
        exceedsBy: formatFileSize(exceedsBy)
      }
      showMergeSizeWarning.value = true
      return false
    }
    return true
  }

  const closeMergeSizeWarning = () => {
    showMergeSizeWarning.value = false
    mergeSizeValidation.value = null
  }

  const proceedWithMerge = () => {
    closeMergeSizeWarning()
    navigateToOrganizePages()
  }

  const navigateToOrganizePages = () => {
    // Track bulk merge started
    trackEvent(MetricEventType.DOCUMENTS_BULK_MERGE_STARTED, {
      document_count: selectedDocuments.value.length,
      case_uuid: store.caseRecord?.uuid,
      document_uuids: selectedDocuments.value.map((d) => d.uuid)
    })

    try {
      // Navigate to organize pages view with document IDs and case ID via router state
      const documentIds = selectedDocuments.value.map((doc) => doc.uuid)
      const caseId = store.caseRecord?.uuid

      if (!caseId) {
        toast({
          title: t('error'),
          description: 'Case ID not found',
          variant: 'destructive'
        })
        return
      }

      // Use route params for caseId and history.state for documentIds
      router.push({
        name: 'Organize Pages',
        params: {
          caseId
        },
        state: {
          documentIds
        }
      })
    } catch (error) {
      console.error('Error navigating to organize pages:', error)
      toast({
        title: t('mergeDocuments'),
        description: t('organizePagesError'),
        variant: 'destructive'
      })
    }
  }

  const handleBulkMerge = async () => {
    if (selectedDocuments.value.length < 2) {
      toast({
        title: t('mergeDocuments'),
        description: t('organizePagesDescription'),
        variant: 'destructive'
      })
      return
    }

    // Check size limit before proceeding
    if (checkMergeSizeLimit()) {
      navigateToOrganizePages()
    }
    // If size exceeds limit, the warning dialog will be shown
  }

  const handleOrganizePages = async (doc: Document) => {
    try {
      const caseId = store.caseRecord?.uuid

      if (!caseId) {
        toast({
          title: t('error'),
          description: 'Case ID not found',
          variant: 'destructive'
        })
        return
      }

      // Track organize pages opened
      trackEvent(MetricEventType.DOCUMENT_ORGANIZE_PAGES_OPENED, {
        document_uuid: doc.uuid,
        document_name: doc.filename,
        case_uuid: store.caseRecord?.uuid
      })

      // Use route params for caseId and history.state for documentIds
      router.push({
        name: 'Organize Pages',
        params: {
          caseId
        },
        state: {
          documentIds: [doc.uuid]
        }
      })
    } catch (error) {
      console.error('Error navigating to organize pages:', error)
      toast({
        title: t('mergeDocuments'),
        description: t('organizePagesError'),
        variant: 'destructive'
      })
    }
  }

  const truncateFilename = (filename: string, maxLength: number = 30) => {
    if (!filename) return ''
    const extension = filename.slice(filename.lastIndexOf('.'))
    const nameWithoutExtension = filename.slice(0, filename.lastIndexOf('.'))
    if (nameWithoutExtension.length <= maxLength) return filename
    return `${nameWithoutExtension.slice(0, maxLength)}...${extension}`
  }

  const handleNameClick = (doc: any) => {
    // Virtual Actaport documents should open in Actaport
    if (doc.is_virtual && doc.actaport_url) {
      window.open(doc.actaport_url, '_blank')
    } else if (doc.uuid) {
      handlePreview(doc)
    }
  }

  const openDeleteModal = (doc: any) => {
    documentToDelete.value = doc
    showDeleteModal.value = true
  }

  const closeDeleteModal = () => {
    showDeleteModal.value = false
    documentToDelete.value = null
  }

  const handleDelete = async () => {
    if (!documentToDelete.value) return
    const docToDelete = documentToDelete.value

    if (docToDelete.uuid) {
      try {
        await DocumentsService.documentsDestroy({
          uuid: docToDelete.uuid
        })

        // Track document deleted
        trackEvent(MetricEventType.DOCUMENT_DELETED, {
          document_uuid: docToDelete.uuid,
          document_name: docToDelete.filename,
          document_type: docToDelete.document_type,
          case_uuid: store.caseRecord?.uuid
        })

        // Optimistically remove the document from store immediately
        store.removeDocumentsOptimistically([docToDelete.uuid])

        closeDeleteModal()

        // Refresh table data with updated store first
        if (tableRef.value) {
          tableRef.value.refreshData()
        }

        // Then refresh case data from backend for consistency (in background)
        setTimeout(async () => {
          try {
            await store.loadCase(store.caseRecord?.uuid || '')
            if (tableRef.value) {
              tableRef.value.refreshData()
            }
          } catch (refreshError) {
            console.error(
              'Background refresh failed (but optimistic update already succeeded):',
              refreshError
            )
          }
        }, 1000)

        // Show success toast
        genericSuccessToast()
      } catch (error) {
        console.error('Error deleting document:', error)
        toast({
          variant: 'destructive',
          title: 'Fehler',
          description: 'Dokument konnte nicht gelöscht werden'
        })
      }
    }
  }

  const openBulkDeleteModal = () => {
    if (selectedDocuments.value.length === 0) {
      toast({
        title: t('error'),
        description: 'No documents selected',
        variant: 'destructive'
      })
      return
    }
    showBulkDeleteModal.value = true
  }

  const closeBulkDeleteModal = () => {
    showBulkDeleteModal.value = false
  }

  const handleBulkDelete = async () => {
    if (selectedDocuments.value.length === 0) return
    const targets = [...selectedDocuments.value]

    // Track bulk delete started
    trackEvent(MetricEventType.DOCUMENTS_BULK_DELETE_STARTED, {
      document_count: targets.length,
      case_uuid: store.caseRecord?.uuid,
      document_uuids: targets.map((d) => d.uuid)
    })

    try {
      // Clear selection immediately to prevent UI issues
      selectedDocuments.value = []

      const results = await Promise.allSettled(
        targets.map((doc) => DocumentsService.documentsDestroy({ uuid: doc.uuid }))
      )

      const successCount = results.filter((r) => r.status === 'fulfilled').length
      const failureCount = results.length - successCount

      // Close modal before refreshing
      closeBulkDeleteModal()

      // Add a small delay to let the backend process the deletions
      await new Promise((resolve) => setTimeout(resolve, 500))

      // Optimistically remove deleted documents from store immediately
      const successfullyDeletedUuids = results
        .map((result, index) => ({ result, doc: targets[index] }))
        .filter(({ result }) => result.status === 'fulfilled')
        .map(({ doc }) => doc.uuid)

      if (successfullyDeletedUuids.length > 0) {
        store.removeDocumentsOptimistically(successfullyDeletedUuids)
      }

      // Refresh table data with updated store first
      if (tableRef.value) {
        try {
          tableRef.value.refreshData()
        } catch (tableError) {
          console.error('Error refreshing table data:', tableError)
        }
      }

      // Then refresh case data from backend to ensure consistency (but with error handling)
      try {
        await store.loadCase(store.caseRecord?.uuid || '')

        // Refresh table one more time with fresh data
        if (tableRef.value) {
          tableRef.value.refreshData()
        }
      } catch (refreshError) {
        console.error(
          'Error refreshing case data from backend (continuing with optimistic updates):',
          refreshError
        )
        // Don't throw here - the optimistic updates should be sufficient
      }

      if (failureCount === 0) {
        // Track bulk delete completed
        trackEvent(MetricEventType.DOCUMENTS_BULK_DELETE_COMPLETED, {
          document_count: successCount,
          case_uuid: store.caseRecord?.uuid
        })

        toast({
          title: t('bulkDeleteSuccess'),
          description: t('bulkDeleteSuccess', { count: successCount }),
          variant: 'default'
        })
      } else {
        // Track bulk delete with failures
        trackEvent(MetricEventType.DOCUMENTS_BULK_DELETE_FAILED, {
          document_count: targets.length,
          success_count: successCount,
          failure_count: failureCount,
          case_uuid: store.caseRecord?.uuid
        })

        toast({
          title: t('bulkDeleteError'),
          description: t('bulkDeleteError', { successCount, failureCount }),
          variant: 'destructive'
        })
      }
    } catch (error) {
      // Defensive: unexpected failure path
      console.error('Unexpected error in bulk delete operation:', error)

      // Track bulk delete failed
      trackEvent(MetricEventType.DOCUMENTS_BULK_DELETE_FAILED, {
        document_count: targets.length,
        error: error instanceof Error ? error.message : 'Unknown error',
        case_uuid: store.caseRecord?.uuid
      })

      closeBulkDeleteModal()
      selectedDocuments.value = []
      toast({
        variant: 'destructive',
        title: t('bulkDeleteError')
      })
    }
  }

  const handleBulkRename = async () => {
    if (selectedDocuments.value.length === 0 || isBulkRenaming.value) return

    isBulkRenaming.value = true

    // Track bulk rename started
    trackEvent(MetricEventType.DOCUMENTS_BULK_RENAME_STARTED, {
      document_count: selectedDocuments.value.length,
      case_uuid: store.caseRecord?.uuid,
      document_uuids: selectedDocuments.value.map((d) => d.uuid)
    })

    try {
      // Show loading state
      const loadingToast = toast({
        title: t('renameWithAI'),
        description: `Processing ${selectedDocuments.value.length} documents...`
      })

      // Generate AI titles for all selected documents
      const renamePromises = selectedDocuments.value.map(async (doc) => {
        try {
          // Generate AI title
          const titleResponse = await DocumentsService.documentsGenerateTitleCreate({
            uuid: doc.uuid
          })

          if (titleResponse.suggested_title) {
            // Update the document with the new title
            await DocumentsService.documentsUpdate({
              uuid: doc.uuid,
              requestBody: {
                filename: `${titleResponse.suggested_title}.pdf`,
                title: titleResponse.suggested_title
              }
            })

            return { success: true, doc, newTitle: titleResponse.suggested_title }
          }
          return { success: false, doc, error: 'No suggested title' }
        } catch (error) {
          console.error(`Error renaming document ${doc.uuid}:`, error)
          return {
            success: false,
            doc,
            error: error instanceof Error ? error.message : 'Unknown error'
          }
        }
      })

      const results = await Promise.allSettled(renamePromises)
      const successCount = results.filter((r) => r.status === 'fulfilled' && r.value.success).length
      const failureCount = results.length - successCount

      // Update the local store for instant UI feedback
      if (store.documents) {
        results.forEach((result) => {
          if (
            result.status === 'fulfilled' &&
            result.value.success &&
            result.value.newTitle &&
            store.documents
          ) {
            const docIndex = store.documents.findIndex(
              (d: Document) => d.uuid === result.value.doc.uuid
            )
            if (docIndex !== -1) {
              store.documents[docIndex] = {
                ...store.documents[docIndex],
                filename: `${result.value.newTitle}.pdf`,
                title: result.value.newTitle
              }
            }
          }
        })
      }

      // Refresh the case data to ensure everything is in sync
      await store.loadCase(store.caseRecord?.uuid || '')

      // Refresh the table to show updated data
      if (tableRef.value) {
        tableRef.value.refreshData()
      }

      // Keep only successfully renamed documents selected so user knows which ones were affected
      const successfullyRenamedUuids = results
        .filter((r: any) => r.status === 'fulfilled' && r.value.success)
        .map((r: any) => r.value.doc.uuid)

      selectedDocuments.value = selectedDocuments.value.filter((doc: any) =>
        successfullyRenamedUuids.includes(doc.uuid)
      )

      // Close loading toast and show result
      loadingToast.dismiss()

      if (failureCount === 0) {
        // Track bulk rename completed
        trackEvent(MetricEventType.DOCUMENTS_BULK_RENAME_COMPLETED, {
          document_count: successCount,
          case_uuid: store.caseRecord?.uuid
        })

        toast({
          title: t('bulkRenameSuccess'),
          description: `${successCount} documents renamed successfully`,
          variant: 'default'
        })
      } else {
        // Track bulk rename with failures
        trackEvent(MetricEventType.DOCUMENTS_BULK_RENAME_FAILED, {
          document_count: selectedDocuments.value.length,
          success_count: successCount,
          failure_count: failureCount,
          case_uuid: store.caseRecord?.uuid
        })

        toast({
          title: t('bulkRenameError'),
          description: `${successCount} renamed, ${failureCount} failed`,
          variant: 'destructive'
        })
      }
    } catch (error) {
      console.error('Error in bulk rename:', error)

      // Track bulk rename failed
      trackEvent(MetricEventType.DOCUMENTS_BULK_RENAME_FAILED, {
        document_count: selectedDocuments.value.length,
        error: error instanceof Error ? error.message : 'Unknown error',
        case_uuid: store.caseRecord?.uuid
      })

      toast({
        title: t('bulkRenameError'),
        description: 'Failed to rename documents',
        variant: 'destructive'
      })
    } finally {
      isBulkRenaming.value = false
    }
  }

  const handlePreview = (doc: any) => {
    currentPdfUuid.value = doc.uuid
    currentDocument.value = doc
    // Use current_pdf_url if available (modified version), otherwise use original url
    currentPdfUrl.value = doc.current_pdf_url || doc.url
    currentPdfFilename.value = doc.filename
    showPdfViewer.value = true

    // Track document preview opened
    trackEvent(MetricEventType.DOCUMENT_PREVIEW_OPENED, {
      document_uuid: doc.uuid,
      document_name: doc.filename,
      document_type: doc.document_type,
      case_uuid: store.caseRecord?.uuid,
      has_modified_version: !!doc.current_pdf_url
    })
  }

  const handleDocumentUpdated = async (updatedDocument: any) => {
    // Update the document in the local store immediately for instant UI feedback
    if (store.documents) {
      const docIndex = store.documents.findIndex((d: Document) => d.uuid === updatedDocument.uuid)
      if (docIndex !== -1) {
        store.documents[docIndex] = {
          ...store.documents[docIndex],
          ...updatedDocument
        }
      }
    }

    // Update the current document and PDF URL if this is the document being viewed
    if (updatedDocument.uuid === currentPdfUuid.value) {
      currentDocument.value = updatedDocument
      currentPdfUrl.value = updatedDocument.current_pdf_url || updatedDocument.url
    }

    // Refresh table data to show updated modification status
    if (tableRef.value) {
      tableRef.value.refreshData()
    }
  }

  const handleDownload = async (doc: any) => {
    try {
      genericSuccessToast()

      // Track document download
      trackEvent(MetricEventType.DOCUMENT_DOWNLOADED, {
        document_uuid: doc.uuid,
        document_name: doc.filename,
        document_type: doc.document_type,
        file_size_bytes: doc.filesize || 0,
        case_uuid: store.caseRecord?.uuid
      })

      const response = await fetch(doc.url)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = doc.filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading document:', error)
    }
  }

  const handleSendToActaport = async (doc: Document) => {
    try {
      syncingDocuments.value.add(doc.uuid)

      // Start tracking this operation
      syncNotifications.startOperations(1)

      const response = await DocumentsService.documentsSendToActaportCreate({
        uuid: doc.uuid
      })

      if (response.status === SendToActaportResponseStatusEnum.ALREADY_SYNCED) {
        syncingDocuments.value.delete(doc.uuid)

        if (store.documents) {
          const docIndex = store.documents.findIndex((d: Document) => d.uuid === doc.uuid)
          if (docIndex !== -1) {
            store.documents[docIndex] = {
              ...store.documents[docIndex],
              platform_sync_status: PlatformSyncStatusEnum.IN_BOTH
            }
          }
        }

        // Document was already synced - complete the operation as successful
        syncNotifications.completeOperation(true)
        tableRef.value?.refreshData()
      }
      // Otherwise, success will be handled by WebSocket listener
    } catch (error: any) {
      syncingDocuments.value.delete(doc.uuid)
      // Use banner notification with failed operation details for retry
      syncNotifications.completeOperation(
        false,
        error.body?.error || 'Could not send document to Actaport',
        {
          type: 'send',
          documentUuid: doc.uuid,
          document: doc
        }
      )
      throw error
    }
  }

  const handleImportFromActaport = async (actaportDoc: any) => {
    try {
      importingDocuments.value.add(actaportDoc.actaport_id)

      // Start tracking this operation
      syncNotifications.startOperations(1)

      await DocumentsService.documentsImportFromActaportCreate({
        caseUuid: caseUuid.value,
        requestBody: {
          actaport_document_id: actaportDoc.actaport_id
        }
      })
      // Success will be handled by WebSocket listener
    } catch (error: any) {
      importingDocuments.value.delete(actaportDoc.actaport_id)
      // Use banner notification with failed operation details for retry
      syncNotifications.completeOperation(
        false,
        error.body?.message || t('importErrorDescription'),
        {
          type: 'import',
          actaportId: actaportDoc.actaport_id,
          document: actaportDoc
        }
      )
      throw error
    }
  }

  const refreshActaportDocuments = async () => {
    if (!caseUuid.value) return

    isRefreshingActaport.value = true
    try {
      const response = await DocumentsService.documentsRefreshActaportDocumentsCreate({
        caseUuid: caseUuid.value
      })

      // Update virtual documents from response
      virtualDocuments.value = (response as any).virtual_documents || []
      lastActaportRefresh.value = new Date()

      // Force table to refetch data
      if (tableRef.value?.refreshData) {
        tableRef.value.refreshData()
      }

      toast({
        title: t('refreshSuccess'),
        description: t('refreshSuccessDescription')
      })
    } catch (error: any) {
      toast({
        title: t('refreshError'),
        description: error.body?.error || t('refreshErrorDescription'),
        variant: 'destructive'
      })
    } finally {
      isRefreshingActaport.value = false
    }
  }

  const handleBulkSyncPlatforms = async () => {
    if (selectedDocuments.value.length === 0) return

    // Separate documents by what needs to be done
    const jupusOnlyDocs = selectedDocuments.value.filter(
      (doc) => doc.platform_sync_status === PlatformSyncStatusEnum.ONLY_JUPUS && doc.uuid
    )
    const actaportOnlyDocs = selectedDocuments.value.filter(
      (doc) => doc.platform_sync_status === PlatformSyncStatusEnum.ONLY_ACTAPORT && doc.actaport_id
    )

    const totalToSync = jupusOnlyDocs.length + actaportOnlyDocs.length

    if (totalToSync === 0) {
      // Keep this one toast for the edge case where nothing needs syncing
      toast({
        title: t('error'),
        description: 'No documents need syncing',
        variant: 'destructive'
      })
      return
    }

    // Clear selection immediately to prevent UI issues
    selectedDocuments.value = []

    const document_uuids = jupusOnlyDocs.map((doc) => doc.uuid)
    const actaport_document_ids = actaportOnlyDocs.map((doc) => doc.actaport_id)

    // Add documents to tracking Sets so they show SYNCING status
    jupusOnlyDocs.forEach((doc) => syncingDocuments.value.add(doc.uuid))
    actaportOnlyDocs.forEach((doc) => importingDocuments.value.add(doc.actaport_id))

    // Start tracking all operations with the banner
    syncNotifications.startOperations(totalToSync)

    try {
      // Call bulk sync endpoint - WebSocket will handle progress updates
      await DocumentsService.documentsBulkSyncPlatformsCreate({
        caseUuid: caseUuid.value,
        requestBody: {
          document_uuids,
          actaport_document_ids
        }
      })

      // WebSocket will update banner status as documents complete
    } catch (error) {
      console.error('Error in bulk sync:', error)

      // Mark all operations as failed since bulk sync failed to start
      for (let i = 0; i < totalToSync; i++) {
        syncNotifications.completeOperation(
          false,
          error instanceof Error ? error.message : 'Failed to start bulk sync'
        )
      }

      // Clear tracking Sets since bulk sync failed to start
      jupusOnlyDocs.forEach((doc) => syncingDocuments.value.delete(doc.uuid))
      actaportOnlyDocs.forEach((doc) => importingDocuments.value.delete(doc.actaport_id))
    }
  }

  // Documents are now fetched automatically via Case serializer
  // No separate fetchActaportDocuments needed

  const showTemplateModal = ref(false)
  const showQuestionnaireModal = ref(false)
  const templateSearch = ref('')
  const questionnaireSearch = ref('')
  const forms = ref<any[]>([])
  const questionnaires = ref<any[]>([])
  const isLoadingForms = ref(false)
  const isLoadingQuestionnaires = ref(false)

  const fetchForms = async () => {
    isLoadingForms.value = true
    try {
      const response = await FormsService.formsList({
        tag: 'MASTER' // Only fetch MASTER forms
      })
      forms.value = response.results
    } catch (error) {
      console.error('Error fetching forms:', error)
    } finally {
      isLoadingForms.value = false
    }
  }

  const fetchQuestionnaires = async () => {
    isLoadingQuestionnaires.value = true
    try {
      const response = await QuestionnairesService.questionnairesList({ limit: 0 })
      questionnaires.value = response.results
    } catch (error) {
      console.error('Error fetching questionnaires:', error)
    } finally {
      isLoadingQuestionnaires.value = false
    }
  }

  const handleCreateLink = async (type: 'document' | 'questionnaire') => {
    if (type === 'document') {
      showTemplateModal.value = true
      await fetchForms()
    } else if (type === 'questionnaire') {
      showQuestionnaireModal.value = true
      await fetchQuestionnaires()
    }
  }

  const handleSelectTemplate = async (form: any) => {
    try {
      await copyToClipboard(async () => {
        const response = await FormsService.formsThirdPartyTokenRetrieve({
          id: form.id
        })
        return `${window.location.origin}/client/portal/external/pdf/${response.creation_token}/${store.caseRecord?.uuid}`
      })

      toast({
        title: 'Link kopiert',
        description: 'Der Link wurde in die Zwischenablage kopiert',
        variant: 'default'
      })

      showTemplateModal.value = false
    } catch (error) {
      console.error('Error generating shareable link:', error)
      toast({
        title: 'Fehler',
        description: 'Der Link konnte nicht generiert werden',
        variant: 'destructive'
      })
    }
  }

  const handleSelectQuestionnaire = async (questionnaire: any) => {
    try {
      await copyToClipboard(async () => {
        const response = await QuestionnairesService.questionnairesThirdPartyCreate({
          requestBody: {
            questionnaire_id: questionnaire.uuid,
            case_id: store.caseRecord?.uuid || ''
          }
        })
        return `${window.location.origin}/client/portal/external/questionnaire/${response.short_id}?access_token=${response.third_party_access_token}`
      })

      toast({
        title: 'Link kopiert',
        description: 'Der Link wurde in die Zwischenablage kopiert',
        variant: 'default'
      })

      showQuestionnaireModal.value = false
    } catch (error) {
      console.error('Error generating shareable link:', error)
      toast({
        title: 'Fehler',
        description: 'Der Link konnte nicht generiert werden',
        variant: 'destructive'
      })
    }
  }

  const openFormInNewTab = (form: any) => {
    window.open(form.file, '_blank')
  }

  const openQuestionnaireInNewTab = (questionnaire: any) => {
    const preview_url = `${window.location.origin}/client/portal/questionnaire/preview/${questionnaire.uuid}`
    window.open(preview_url, '_blank')
  }

  const newPdfEditorEnabled = useFeatureFlag(FlagEnum.NEW_PDF_EDITOR)

  onMounted(async () => {
    newPdfEditorEnabled.checkFlag()
    actaportV2TwoWaySyncEnabled.checkFlag()
  })

  // Initialize sync state from backend when case loads or refreshes
  watch(
    () => store.caseRecord,
    (caseData) => {
      if (caseData) {
        // Initialize virtual documents from backend
        if ((caseData as any).virtual_documents) {
          virtualDocuments.value = (caseData as any).virtual_documents
        }
        // Initialize syncing documents from backend
        const backendSyncingUuids = (caseData as any).syncing_document_uuids || []
        const backendImportingIds = (caseData as any).importing_actaport_ids || []

        // Update the Sets
        syncingDocuments.value = new Set(backendSyncingUuids)
        importingDocuments.value = new Set(backendImportingIds)

        // Initialize banner with total count of in-progress operations
        const totalSyncing = backendSyncingUuids.length + backendImportingIds.length
        if (totalSyncing > 0) {
          syncNotifications.reset()
          syncNotifications.startOperations(totalSyncing)
        }
      }
    },
    { immediate: true }
  )

  watch(
    () => store.isLoadingDocuments,
    async (isLoading, wasLoading) => {
      if (wasLoading && !isLoading) {
        await nextTick()
        if (tableRef.value) {
          tableRef.value.refreshData()
        }
      }
    }
  )

  // Track when documents are selected/deselected
  watch(
    selectedDocuments,
    (newSelection, oldSelection) => {
      const newCount = newSelection.length
      const oldCount = oldSelection?.length || 0

      // Only track if selection increased (documents were selected)
      if (newCount > oldCount && newCount > 0) {
        const addedCount = newCount - oldCount
        trackEvent(MetricEventType.DOCUMENTS_BULK_SELECTED, {
          document_count: newCount,
          added_count: addedCount,
          case_uuid: store.caseRecord?.uuid,
          document_uuids: newSelection.map((d) => d.uuid)
        })
      }
      // Track if all documents were deselected
      else if (newCount === 0 && oldCount > 0) {
        trackEvent(MetricEventType.DOCUMENTS_BULK_DESELECTED, {
          previous_count: oldCount,
          case_uuid: store.caseRecord?.uuid
        })
      }
    },
    { deep: true }
  )

  // Track when PDF viewer is closed
  watch(showPdfViewer, (isOpen, wasOpen) => {
    if (wasOpen && !isOpen && currentPdfUuid.value) {
      trackEvent(MetricEventType.DOCUMENT_PREVIEW_CLOSED, {
        document_uuid: currentPdfUuid.value,
        document_name: currentPdfFilename.value,
        case_uuid: store.caseRecord?.uuid
      })
    }
  })

  watch(platformFilter, (newValue) => {
    const url = new URL(window.location.href)
    url.searchParams.set('platformFilter', newValue)
    window.history.replaceState({}, '', url.toString())

    selectedDocuments.value = []
    tableRef.value?.refreshData()
  })

  // Watch for route changes to refresh data when returning from organize pages
  watch(
    () => route.path,
    async (newPath, oldPath) => {
      // If we just navigated to this documents view, refresh the table
      if (newPath.includes('/documents') && oldPath?.includes('/organize-documents')) {
        // Give the store a moment to update
        await nextTick()
        if (tableRef.value) {
          tableRef.value.refreshData()
        }
      }
    }
  )

  const selectable = computed(() => {
    return true
  })

  const hasReadOnlySelected = computed(() => {
    return selectedDocuments.value.some((doc) => isDocumentReadOnly(doc))
  })

  const hasOnlyActaportDocuments = computed(() => {
    // Check if ALL selected documents are Actaport-only (not yet imported to JUPUS)
    return (
      selectedDocuments.value.length > 0 &&
      selectedDocuments.value.every(
        (doc) => doc.platform_sync_status === PlatformSyncStatusEnum.ONLY_ACTAPORT
      )
    )
  })

  const hasOnlyJupusDocuments = computed(() => {
    // Check if ALL selected documents are JUPUS-only (not yet sent to Actaport)
    return (
      selectedDocuments.value.length > 0 &&
      selectedDocuments.value.every(
        (doc) => doc.platform_sync_status === PlatformSyncStatusEnum.ONLY_JUPUS
      )
    )
  })

  const hasAnyActaportOnlyDocuments = computed(() => {
    // Check if ANY selected documents are Actaport-only
    return selectedDocuments.value.some(
      (doc) => doc.platform_sync_status === PlatformSyncStatusEnum.ONLY_ACTAPORT
    )
  })

  const hasAnyJupusOnlyDocuments = computed(() => {
    // Check if ANY selected documents are JUPUS-only
    return selectedDocuments.value.some(
      (doc) => doc.platform_sync_status === PlatformSyncStatusEnum.ONLY_JUPUS
    )
  })

  const hasInBothDocuments = computed(() => {
    // Check if ANY selected documents are already synced (IN_BOTH)
    return selectedDocuments.value.some(
      (doc) => doc.platform_sync_status === PlatformSyncStatusEnum.IN_BOTH
    )
  })

  const hasOnlyInBothDocuments = computed(() => {
    // Check if ALL selected documents are already synced (IN_BOTH)
    return (
      selectedDocuments.value.length > 0 &&
      selectedDocuments.value.every(
        (doc) => doc.platform_sync_status === PlatformSyncStatusEnum.IN_BOTH
      )
    )
  })

  const hasMixedPlatformDocuments = computed(() => {
    // Show "Sync Between Platforms" when there's a mix of different platform statuses
    // This includes combinations like:
    // - JUPUS-only + IN_BOTH
    // - Actaport-only + IN_BOTH
    // - JUPUS-only + Actaport-only
    // - All three types together
    const hasMultipleStatuses =
      [
        hasAnyJupusOnlyDocuments.value,
        hasAnyActaportOnlyDocuments.value,
        hasInBothDocuments.value
      ].filter(Boolean).length > 1

    return hasMultipleStatuses
  })

  const canSyncBetweenPlatforms = computed(() => {
    // Disable sync if ANY "In Both" documents are selected (no re-imports or re-exports allowed)
    // This prevents re-syncing documents that are already in both platforms
    if (hasInBothDocuments.value) {
      return false
    }
    // Enable sync if there are any JUPUS-only or Actaport-only documents (but no In Both)
    return hasAnyJupusOnlyDocuments.value || hasAnyActaportOnlyDocuments.value
  })

  const canMergeDocuments = computed(() => {
    // Need at least 2 documents to merge
    if (selectedDocuments.value.length < 2) return false
    // Cannot merge if any read-only document is selected
    if (hasReadOnlySelected.value) return false
    // Cannot merge if any Actaport-only documents are selected
    if (hasAnyActaportOnlyDocuments.value) return false
    return true
  })

  const platformFilterCounts = computed(() => {
    const jupusDocuments = store.documents || []
    const allDocs = [...jupusDocuments, ...virtualDocuments.value]
    const filtered = filterDocuments(allDocs)

    return {
      all: filtered.length,
      inBoth: filtered.filter((doc) => doc.platform_sync_status === PlatformSyncStatusEnum.IN_BOTH)
        .length,
      onlyJupus: filtered.filter(
        (doc) => doc.platform_sync_status === PlatformSyncStatusEnum.ONLY_JUPUS
      ).length,
      onlyActaport: filtered.filter(
        (doc) => doc.platform_sync_status === PlatformSyncStatusEnum.ONLY_ACTAPORT
      ).length
    }
  })

  const handleSyncBannerRetry = async () => {
    // Get all failed operations before resetting
    const failedOps = syncNotifications.getFailedOperations()

    if (failedOps.length === 0) {
      // No failed operations to retry
      syncNotifications.reset()
      return
    }

    // Reset the sync state before retrying
    syncNotifications.reset()

    // Start tracking the retry operations
    syncNotifications.startOperations(failedOps.length)

    // Retry each failed operation
    for (const failedOp of failedOps) {
      try {
        if (failedOp.type === 'send' && failedOp.document) {
          // Retry sending document to Actaport
          await handleSendToActaport(failedOp.document)
        } else if (failedOp.type === 'import' && failedOp.document) {
          // Retry importing document from Actaport
          await handleImportFromActaport(failedOp.document)
        }
      } catch (error) {
        // Error handling is done in the individual functions
        // Just continue with the next operation
      }
    }
  }

  const handleSyncBannerDismiss = () => {
    syncNotifications.reset()
  }
</script>

<template>
  <div class="mt-5 min-h-screen w-full space-y-6">
    <!-- Action Buttons -->
    <div
      class="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:space-y-0"
    >
      <div class="flex items-center gap-2">
        <h2 class="text-base font-semibold text-gray-900">{{ t('title') }}</h2>
        <HelpLink
          :url="KNOWLEDGE_BASE_URLS.documents"
          :tooltip="t('helpDocuments')"
          location="documents"
        />
      </div>
      <div class="flex flex-col space-y-2 sm:flex-row sm:items-center sm:gap-3 sm:space-y-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="secondary"
              class="h-8 gap-2 rounded-lg border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700
                shadow-sm hover:bg-gray-50 sm:w-auto"
            >
              <Link2 class="h-4 w-4" />
              {{ t('createLink') }}
              <ChevronDown class="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            class="w-[200px]"
          >
            <DropdownMenuItem
              class="gap-2"
              @click="handleCreateLink('document')"
            >
              <FileText class="h-4 w-4" />
              {{ t('documentTemplate') }}
            </DropdownMenuItem>
            <DropdownMenuItem
              class="gap-2"
              @click="handleCreateLink('questionnaire')"
            >
              <FileQuestion class="h-4 w-4" />
              {{ t('questionnaire') }}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button
          v-if="enableLawSpecificData"
          variant="secondary"
          class="flex h-8 w-full items-center justify-center gap-2 rounded-lg border-gray-200 bg-white px-3 py-1.5
            text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 sm:w-auto"
          @click="handleDocumentGeneration"
        >
          <FileText class="h-4 w-4" />
          {{ t('generateDocument') }}
          <Badge class="h-5 rounded border bg-purple-500 px-1.5 py-0.5 text-xs text-white">
            KI
          </Badge>
        </Button>
        <Button
          variant="secondary"
          class="flex h-8 w-full items-center justify-center gap-2 rounded-lg border-gray-200 bg-white px-3 py-1.5
            text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 sm:w-auto"
          @click="showUploadModal = true"
        >
          <CloudUpload class="h-4 w-4" />
          {{ t('uploadDocuments') }}
        </Button>
        <Button
          v-if="showPlatformStatusColumn"
          variant="secondary"
          class="flex h-8 w-full items-center justify-center gap-2 rounded-lg border-gray-200 bg-white px-3 py-1.5
            text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 sm:w-auto"
          :disabled="isRefreshingActaport"
          @click="refreshActaportDocuments"
        >
          <RefreshCw
            :class="{ 'animate-spin': isRefreshingActaport }"
            class="h-4 w-4"
          />
          {{ isRefreshingActaport ? t('refreshing') : t('refreshFromActaport') }}
        </Button>
      </div>
    </div>

    <div class="relative rounded-xl bg-white p-3 shadow-sm ring-1 ring-gray-900/5 sm:p-4 lg:p-5">
      <!-- Bulk Actions Overlay -->
      <div
        v-if="selectedDocuments.length > 0"
        class="absolute left-4 top-3 z-50 flex w-fit items-stretch rounded-lg border border-gray-200 bg-white/95
          pl-4 shadow-lg ring-1 ring-gray-900/5 backdrop-blur-sm"
      >
        <div class="flex items-center gap-2 py-2.5">
          <span class="whitespace-nowrap text-sm font-semibold text-gray-900">
            {{ selectedDocuments.length }} {{ t('documents') }} {{ t('selected') }}
          </span>
        </div>
        <div class="ml-4 flex items-stretch divide-x divide-gray-300">
          <TooltipProvider v-if="hasAnyActaportOnlyDocuments">
            <Tooltip>
              <TooltipTrigger as-child>
                <span class="inline-flex">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled
                    class="flex h-full items-center gap-1.5 rounded-none px-3 py-2.5 text-sm font-medium text-gray-700
                      hover:bg-gray-100 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50
                      disabled:hover:bg-transparent"
                  >
                    <AiEditIcon class="h-3.5 w-3.5" />
                    {{ t('bulkRename') }}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent class="border-black bg-black text-white">
                <p class="text-white">Cannot rename virtual Actaport documents</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Button
            v-else
            variant="ghost"
            size="sm"
            :disabled="isBulkRenaming"
            class="flex h-full items-center gap-1.5 rounded-none px-3 py-2.5 text-sm font-medium text-gray-700
              hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
            @click="handleBulkRename"
          >
            <Loader
              v-if="isBulkRenaming"
              class="h-3.5 w-3.5 animate-spin"
            />
            <AiEditIcon
              v-else
              class="h-3.5 w-3.5"
            />
            {{ t('bulkRename') }}
          </Button>

          <TooltipProvider v-if="hasAnyActaportOnlyDocuments">
            <Tooltip>
              <TooltipTrigger as-child>
                <span class="inline-flex">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled
                    class="flex h-full items-center gap-1.5 rounded-none px-3 py-2.5 text-sm font-medium text-gray-700
                      hover:bg-gray-100 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50
                      disabled:hover:bg-transparent"
                  >
                    <Download class="h-3.5 w-3.5" />
                    {{ t('download') }}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent class="border-black bg-black text-white">
                <p class="text-white">Cannot download virtual Actaport documents</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Button
            v-else
            variant="ghost"
            size="sm"
            class="flex h-full items-center gap-1.5 rounded-none px-3 py-2.5 text-sm font-medium text-gray-700
              hover:bg-gray-100"
            @click="handleBulkDownload"
          >
            <Download class="h-3.5 w-3.5" />
            {{ t('download') }}
          </Button>

          <template v-if="newPdfEditorEnabled.isEnabled">
            <TooltipProvider v-if="!canMergeDocuments">
              <Tooltip>
                <TooltipTrigger as-child>
                  <span class="inline-flex">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled
                      class="flex h-full items-center gap-1.5 rounded-none px-3 py-2.5 text-sm font-medium text-gray-700
                        hover:bg-gray-100 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50
                        disabled:hover:bg-transparent"
                    >
                      <MergeIcon class="h-3.5 w-3.5" />
                      {{ t('mergeDocuments') }}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent class="border-black bg-black text-white">
                  <p class="text-white">
                    {{ hasReadOnlySelected ? t('cannotMergeReadOnly') : t('cannotMergeSingle') }}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button
              v-else-if="newPdfEditorEnabled.isEnabled"
              variant="ghost"
              size="sm"
              class="flex h-full items-center gap-1.5 rounded-none px-3 py-2.5 text-sm font-medium text-gray-700
                hover:bg-gray-100"
              @click="handleBulkMerge"
            >
              <MergeIcon class="h-3.5 w-3.5" />
              {{ t('mergeDocuments') }}
            </Button>
          </template>
          <template v-if="hasMixedPlatformDocuments && showPlatformStatusColumn">
            <Button
              variant="ghost"
              size="sm"
              class="flex h-full items-center gap-1.5 rounded-none px-3 py-2.5 text-sm font-medium text-gray-700
                hover:bg-gray-100"
              :disabled="!canSyncBetweenPlatforms"
              @click="handleBulkSyncPlatforms"
            >
              <ArrowLeftRight class="h-3.5 w-3.5" />
              {{ t('bulkSyncPlatforms') }}
            </Button>
          </template>

          <template
            v-else-if="
              hasAnyJupusOnlyDocuments && !hasAnyActaportOnlyDocuments && showPlatformStatusColumn
            "
          >
            <Button
              variant="ghost"
              size="sm"
              class="flex h-full items-center gap-1.5 rounded-none px-3 py-2.5 text-sm font-medium text-gray-700
                hover:bg-gray-100"
              :disabled="!canSyncBetweenPlatforms"
              @click="handleBulkSyncPlatforms"
            >
              <ActaportDarkIcon class="h-3.5 w-3.5" />
              {{ t('bulkSendToActaport') }}
            </Button>
          </template>

          <template
            v-else-if="
              hasAnyActaportOnlyDocuments && !hasAnyJupusOnlyDocuments && showPlatformStatusColumn
            "
          >
            <Button
              variant="ghost"
              size="sm"
              class="flex h-full items-center gap-1.5 rounded-none px-3 py-2.5 text-sm font-medium text-gray-700
                hover:bg-gray-100"
              :disabled="!canSyncBetweenPlatforms"
              @click="handleBulkSyncPlatforms"
            >
              <ActaportDarkIcon class="h-3.5 w-3.5" />
              {{ t('bulkImportFromActaport') }}
            </Button>
          </template>

          <template v-else-if="hasOnlyInBothDocuments && showPlatformStatusColumn">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger as-child>
                  <span class="inline-flex">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled
                      class="flex h-full items-center gap-1.5 rounded-none px-3 py-2.5 text-sm font-medium text-gray-700
                        disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50
                        disabled:hover:bg-transparent"
                    >
                      <ArrowLeftRight class="h-3.5 w-3.5" />
                      {{ t('bulkSyncPlatforms') }}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent class="border-black bg-black text-white">
                  <p class="text-white">All selected documents are already synced</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </template>

          <TooltipProvider v-if="hasReadOnlySelected || hasAnyActaportOnlyDocuments">
            <Tooltip>
              <TooltipTrigger as-child>
                <span class="inline-flex">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled
                    class="flex h-full items-center gap-1.5 rounded-none px-3 py-2.5 text-sm font-medium text-red-600
                      hover:bg-red-50 hover:text-red-700 disabled:pointer-events-none disabled:cursor-not-allowed
                      disabled:opacity-50 disabled:hover:bg-transparent"
                  >
                    <Trash2 class="h-3.5 w-3.5" />
                    {{ t('bulkDelete') }}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent class="border-black bg-black text-white">
                <p class="text-white">
                  {{
                    hasAnyActaportOnlyDocuments
                      ? 'Cannot delete virtual Actaport documents'
                      : t('cannotDeleteReadOnly')
                  }}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Button
            v-else
            variant="ghost"
            size="sm"
            class="flex h-full items-center gap-1.5 rounded-none px-3 py-2.5 text-sm font-medium text-red-600
              hover:bg-red-50 hover:text-red-700"
            @click="openBulkDeleteModal"
          >
            <Trash2 class="h-3.5 w-3.5" />
            {{ t('bulkDelete') }}
          </Button>
        </div>
      </div>

      <!-- Table -->
      <PaginatedTableView
        ref="tableRef"
        v-model:selected-items="selectedDocuments"
        :fetch-data="fetchDocuments"
        :selectable="selectable"
        searchable-fields="filename"
        class="overflow-x-auto rounded-xl border-none bg-white"
      >
        <template #actions>
          <div class="flex items-center gap-2">
            <div
              class="flex h-9 cursor-pointer items-center rounded-lg border border-gray-200 bg-white px-2.5
                hover:bg-gray-50"
            >
              <CheckboxField
                v-model="showReadOnlyDocuments"
                name="showReadOnlyDocuments"
                :label="t('showReadOnlyDocuments')"
                @update:model-value="handleShowReadOnlyChange"
              />
            </div>
            <PlatformFilterDropdown
              v-if="showPlatformStatusColumn"
              v-model="platformFilter"
              :counts="platformFilterCounts"
            />
          </div>
        </template>

        <template #before-table>
          <!-- Sync Status Banner -->
          <SyncStatusBanner
            :status="syncNotifications.status.value"
            :count="syncNotifications.count.value"
            :error-message="syncNotifications.errorMessage.value"
            @retry="handleSyncBannerRetry"
            @dismiss="handleSyncBannerDismiss"
          />
        </template>

        <template #table-head>
          <th class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">
            <div class="flex items-center gap-1">
              {{ t('name') }}
            </div>
          </th>
          <th class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
            <div class="flex items-center gap-1">
              {{ t('source') }}
            </div>
          </th>
          <th class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
            <div class="flex items-center gap-1">
              {{ t('date') }}
            </div>
          </th>
          <th
            v-if="showPlatformStatusColumn"
            class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
          >
            <div class="flex items-center gap-1">
              {{ t('platformStatus') }}
            </div>
          </th>
          <th class="relative px-3 py-3.5">
            <span class="sr-only">{{ t('actions') }}</span>
          </th>
        </template>

        <template #table-row="{ items, toggleSelectItem, isItemSelected }">
          <TableRow
            v-for="doc in items"
            :key="doc.uuid || doc.actaport_id"
            class="group hover:bg-gray-50"
          >
            <!-- Checkbox column -->
            <TableCell class="w-12 px-4 py-4">
              <input
                type="checkbox"
                :checked="isItemSelected(doc)"
                class="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                @change="toggleSelectItem(doc)"
              />
            </TableCell>

            <!-- Name column -->
            <TableCell class="py-4 pl-4 pr-3">
              <!-- Edit mode -->
              <div
                v-if="doc.uuid && editingDocument === doc.uuid"
                class="flex w-full max-w-xl items-center gap-3"
              >
                <div
                  class="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded border
                    border-gray-200 shadow-sm"
                  :class="
                    doc.platform_sync_status === PlatformSyncStatusEnum.ONLY_ACTAPORT
                      ? 'border-blue-200 bg-blue-50'
                      : 'bg-gray-50'
                  "
                >
                  <!-- Actaport-only document thumbnail -->
                  <div
                    v-if="doc.platform_sync_status === PlatformSyncStatusEnum.ONLY_ACTAPORT"
                    class="flex h-full w-full items-center justify-center"
                  >
                    <ActaportDarkIcon class="h-6 w-6" />
                  </div>
                  <!-- Regular JUPUS document thumbnail -->
                  <img
                    v-else-if="doc.images && doc.images.length > 0"
                    :src="
                      doc.images.find((img: any) => img.image_preset === 'thumbnail')?.url ||
                      doc.images[0].url
                    "
                    :alt="doc.filename"
                    class="h-full w-full object-cover"
                  />
                  <!-- Fallback for JUPUS docs without thumbnails -->
                  <div
                    v-else
                    class="flex h-full w-full items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200"
                  >
                    <FileQuestion class="h-5 w-5 text-gray-500" />
                  </div>
                </div>

                <div class="relative min-w-[200px] flex-1 sm:min-w-[300px]">
                  <input
                    :value="newName"
                    type="text"
                    class="h-12 w-full rounded-md border-0 px-3 pr-24 text-gray-900 ring-1 ring-inset ring-purple-300
                      focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm"
                    :title="newName"
                    @input="newName = ($event.target as HTMLInputElement).value"
                  />
                  <span class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">.pdf</span>

                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          class="absolute right-12 top-1/2 h-8 w-8 -translate-y-1/2 text-purple-600 hover:bg-purple-50
                            hover:text-purple-700"
                          :disabled="isGeneratingTitle"
                          @click="generateAITitle(doc)"
                        >
                          <AiEditIcon
                            v-if="!isGeneratingTitle"
                            class="h-4 w-4"
                          />
                          <Loader
                            v-else
                            class="h-4 w-4 animate-spin"
                          />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent class="border-black bg-black text-white">
                        <p class="text-white">{{ t('renameWithAI') }}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>

                <div class="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    class="h-10 w-10 text-green-600 hover:text-green-700"
                    @click="saveNewName(doc)"
                  >
                    <CheckCircle2 class="h-5 w-5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    class="h-10 w-10 text-red-600 hover:text-red-700"
                    @click="cancelEditing()"
                  >
                    <X class="h-5 w-5" />
                  </Button>
                </div>
              </div>

              <!-- Normal display mode -->
              <div
                v-else
                class="flex w-full items-center justify-between"
              >
                <div class="flex min-w-0 flex-1 items-center gap-3">
                  <div
                    class="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded border
                      border-gray-200 shadow-sm"
                    :class="
                      doc.platform_sync_status === PlatformSyncStatusEnum.ONLY_ACTAPORT
                        ? 'border-blue-200 bg-blue-50'
                        : 'bg-gray-50'
                    "
                  >
                    <!-- Actaport-only document thumbnail -->
                    <div
                      v-if="doc.platform_sync_status === PlatformSyncStatusEnum.ONLY_ACTAPORT"
                      class="flex h-full w-full items-center justify-center"
                    >
                      <ActaportDarkIcon class="h-6 w-6" />
                    </div>
                    <!-- Regular JUPUS document thumbnail -->
                    <img
                      v-else-if="doc.images && doc.images.length > 0"
                      :src="
                        doc.images.find((img: any) => img.image_preset === 'thumbnail')?.url ||
                        doc.images[0].url
                      "
                      :alt="doc.filename"
                      class="h-full w-full object-cover"
                    />
                    <!-- Fallback icon for JUPUS docs without thumbnails -->
                    <div
                      v-else
                      class="flex h-full w-full items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200"
                    >
                      <FileQuestion class="h-5 w-5 text-gray-500" />
                    </div>
                  </div>

                  <span
                    class="block cursor-pointer truncate font-medium text-gray-900 transition-colors
                      group-hover:text-purple-600 group-hover:underline"
                    :title="doc.filename"
                    @click="handleNameClick(doc)"
                  >
                    {{ truncateFilename(doc.filename, 40) }}
                  </span>

                  <div
                    v-if="doc.uuid && !doc.is_virtual"
                    class="flex flex-shrink-0 gap-2"
                  >
                    <Button
                      variant="ghost"
                      size="icon"
                      class="h-7 w-7 rounded border border-gray-300 bg-white shadow-sm hover:bg-gray-50"
                      @click="startEditing(doc)"
                    >
                      <Pencil class="h-4 w-4 text-black" />
                    </Button>
                  </div>
                </div>
                <div class="flex flex-shrink-0 items-center gap-2">
                  <TooltipProvider v-if="isDocumentReadOnly(doc)">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Lock class="h-4 w-4 flex-shrink-0 text-gray-500" />
                      </TooltipTrigger>
                      <TooltipContent class="border-black bg-black text-white">
                        <p class="text-white">{{ t('readOnlyDocument') }}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <TooltipProvider
                    v-if="isDocumentAiReady(doc)"
                    class="flex-shrink-0"
                  >
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          class="flex h-7 w-7 items-center justify-center rounded-full bg-green-100"
                        >
                          <CheckCircle2 class="h-4 w-4 text-green-600" />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent class="border-black bg-black text-white">
                        <p class="text-white">{{ t('readyForAI') }}</p>
                        <p class="text-xs text-gray-300">{{ t('readyForAIDescription') }}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            </TableCell>

            <!-- Source column -->
            <TableCell class="px-3 py-4">
              <DocumentTypeBadge
                :type="
                  doc.document_type === DocumentTypeEnum.UPLOAD
                    ? doc.upload_origin
                    : doc.document_type
                "
              />
            </TableCell>

            <!-- Date column -->
            <TableCell class="px-3 py-4 text-sm text-gray-900">
              {{ formatDate(doc.created_at) }}
            </TableCell>

            <!-- Platform Status column -->
            <TableCell
              v-if="showPlatformStatusColumn"
              class="px-3 py-4"
            >
              <PlatformStatusBadge
                :status="
                  (syncingDocuments.has(doc.uuid) &&
                    doc.platform_sync_status !== PlatformSyncStatusEnum.IN_BOTH) ||
                  (doc.actaport_id && importingDocuments.has(doc.actaport_id))
                    ? PlatformSyncStatusEnum.SYNCING
                    : doc.platform_sync_status
                "
              />
            </TableCell>

            <!-- Actions column -->
            <TableCell class="px-3 py-4 text-right text-sm">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                  >
                    <span class="sr-only">{{ t('actions') }}</span>
                    <MoreVertical class="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    :disabled="doc.platform_sync_status === PlatformSyncStatusEnum.ONLY_ACTAPORT"
                    @click="handlePreview(doc)"
                  >
                    <Eye class="mr-2 h-4 w-4" />
                    {{ t('preview') }}
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    :disabled="doc.platform_sync_status === PlatformSyncStatusEnum.ONLY_ACTAPORT"
                    @click="handleDownload(doc)"
                  >
                    <Download class="mr-2 h-4 w-4" />
                    {{ t('download') }}
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    v-if="
                      showPlatformStatusColumn &&
                      doc.platform_sync_status === PlatformSyncStatusEnum.ONLY_JUPUS
                    "
                    data-cy="send-to-actaport-action"
                    :disabled="syncingDocuments.has(doc.uuid)"
                    @click="handleSendToActaport(doc)"
                  >
                    <ActaportDarkIcon class="mr-2 h-4 w-4" />
                    {{
                      syncingDocuments.has(doc.uuid) ? t('sendingToActaport') : t('sendToActaport')
                    }}
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    v-if="
                      showPlatformStatusColumn &&
                      doc.platform_sync_status === PlatformSyncStatusEnum.ONLY_ACTAPORT
                    "
                    data-cy="import-from-actaport-action"
                    :disabled="importingDocuments.has(doc.actaport_id)"
                    @click="handleImportFromActaport(doc)"
                  >
                    <ActaportDarkIcon class="mr-2 h-4 w-4" />
                    {{
                      importingDocuments.has(doc.actaport_id)
                        ? t('importingFromActaport')
                        : t('importFromActaport')
                    }}
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    :disabled="
                      isDocumentReadOnly(doc) ||
                      doc.platform_sync_status === PlatformSyncStatusEnum.ONLY_ACTAPORT
                    "
                    @click="handleOrganizePages(doc)"
                  >
                    <FileStack class="mr-2 h-4 w-4" />
                    {{ t('organizePages') }}
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    :disabled="
                      isDocumentReadOnly(doc) ||
                      doc.platform_sync_status === PlatformSyncStatusEnum.ONLY_ACTAPORT
                    "
                    @click="startEditing(doc)"
                  >
                    <Pencil class="mr-2 h-4 w-4" />
                    {{ t('rename') }}
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    :disabled="
                      isDocumentReadOnly(doc) ||
                      doc.platform_sync_status === PlatformSyncStatusEnum.ONLY_ACTAPORT
                    "
                    class="text-red-600 focus:bg-red-50 focus:text-red-600"
                    @click="openDeleteModal(doc)"
                  >
                    <Trash2 class="mr-2 h-4 w-4" />
                    {{ t('delete') }}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </TableCell>
          </TableRow>
        </template>
      </PaginatedTableView>
    </div>
  </div>

  <!-- Delete Confirmation Modal -->
  <Dialog
    :open="showDeleteModal"
    @update:open="closeDeleteModal"
  >
    <DialogContent class="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>{{ t('deleteTitle') }}</DialogTitle>
        <DialogDescription>
          {{ t('description').replace('{filename}', documentToDelete?.filename || '') }}
        </DialogDescription>
      </DialogHeader>
      <DialogFooter class="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
        <Button
          variant="secondary"
          class="mt-2 sm:mt-0"
          @click="closeDeleteModal"
        >
          {{ t('cancel') }}
        </Button>
        <Button
          variant="destructive"
          @click="handleDelete"
        >
          {{ t('confirm') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

  <!-- Bulk Delete Confirmation Modal -->
  <Dialog
    :open="showBulkDeleteModal"
    @update:open="closeBulkDeleteModal"
  >
    <DialogContent class="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>{{ t('bulkDeleteTitle') }}</DialogTitle>
        <DialogDescription>
          {{ t('bulkDeleteDescription').replace('{count}', selectedDocuments.length.toString()) }}
        </DialogDescription>
      </DialogHeader>
      <DialogFooter class="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
        <Button
          variant="secondary"
          class="mt-2 sm:mt-0"
          @click="closeBulkDeleteModal"
        >
          {{ t('cancel') }}
        </Button>
        <Button
          variant="destructive"
          @click="handleBulkDelete"
        >
          {{ t('confirm') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

  <!-- Merge Size Warning Dialog -->
  <Dialog
    :open="showMergeSizeWarning"
    @update:open="closeMergeSizeWarning"
  >
    <DialogContent class="sm:max-w-md">
      <DialogHeader>
        <DialogTitle class="text-amber-600">{{ t('mergeSizeWarning') }}</DialogTitle>
        <DialogDescription class="space-y-2">
          <p>
            {{
              t('mergeSizeWarningDescription', {
                estimatedSize: mergeSizeValidation?.estimatedSize || '',
                maxSize: mergeSizeValidation?.maxSize || ''
              })
            }}
          </p>
          <p class="font-medium text-amber-600">
            {{ t('mergeSizeExceedsBy', { size: mergeSizeValidation?.exceedsBy || '' }) }}
          </p>
        </DialogDescription>
      </DialogHeader>
      <DialogFooter class="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
        <Button
          variant="secondary"
          class="mt-2 sm:mt-0"
          @click="closeMergeSizeWarning"
        >
          {{ t('cancelMerge') }}
        </Button>
        <Button
          variant="destructive"
          @click="proceedWithMerge"
        >
          {{ t('proceedAnyway') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

  <!-- PDF Viewer -->
  <div class="flex h-full w-full">
    <NewPdfViewer
      v-if="newPdfEditorEnabled.isEnabled"
      v-model:isOpen="showPdfViewer"
      v-model:url="currentPdfUrl"
      :filename="currentPdfFilename"
      :document="currentDocument"
      :hide-topbar="isDocumentReadOnly(currentDocument)"
      :disable-edit-page="isDocumentReadOnly(currentDocument)"
      @document-updated="handleDocumentUpdated"
    />
    <PDFViewer
      v-else
      v-model:isOpen="showPdfViewer"
      v-model:url="currentPdfUrl"
      :filename="currentPdfFilename"
      :document="currentDocument"
      @document-updated="handleDocumentUpdated"
    />
  </div>

  <!-- Upload Documents Modal -->
  <Dialog
    :open="showUploadModal"
    @update:open="showUploadModal = false"
  >
    <DialogContent class="sm:max-w-2xl">
      <DialogHeader>
        <DialogTitle>{{ t('uploadDocuments') }}</DialogTitle>
        <DialogDescription>{{ t('uploadDocumentsDescription') }}</DialogDescription>
      </DialogHeader>
      <div class="py-4">
        <DocumentUpload
          v-if="store.caseRecord"
          :case-data="store.caseRecord"
          :auto-remove-after-upload="false"
          @file-uploaded="handleFileUploaded"
        />
      </div>
    </DialogContent>
  </Dialog>

  <TemplateDialog
    v-model="showTemplateModal"
    title="Document Template"
    :description="t('documentTemplateDescription')"
    :items="forms"
    :is-loading="isLoadingForms"
    :search-placeholder="t('documentTemplateSearchPlaceholder')"
    @select-item="handleSelectTemplate"
    @preview-item="openFormInNewTab"
  />

  <TemplateDialog
    v-model="showQuestionnaireModal"
    title="Questionnaire"
    :description="t('documentTemplateDescription')"
    :items="questionnaires"
    :is-loading="isLoadingQuestionnaires"
    :search-placeholder="t('documentTemplateSearchPlaceholder')"
    @select-item="handleSelectQuestionnaire"
    @preview-item="openQuestionnaireInNewTab"
  />
</template>

<style scoped>
  .truncate {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Responsive table styles */
  @media (max-width: 640px) {
    .table-responsive {
      display: block;
      width: 100%;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
    }
  }
</style>
